import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CLOCK_ICON } from "./widgets.constants";

interface AiTimeNudgeProps {
  /** Whether the user can reach the subscription/AI area (controls the CTA). */
  hasSubscriptionAccess: boolean;
}

/** Prompt shown when a subscribed user has not yet used any AI feature. */
export const AiTimeNudge = ({ hasSubscriptionAccess }: AiTimeNudgeProps) => {
  const { t } = useTranslation(["common"]);
  const navigate = useNavigate();

  return (
    <div className="hidden md:block">
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-sxs hover:bg-primary/10 transition-colors"
          >
            <img
              src={CLOCK_ICON}
              alt={t("common:chrome.ai_time_saving")}
              draggable={false}
              className="size-6 object-contain opacity-60"
            />
          </button>
        </PopoverTrigger>
        <PopoverPortal>
          <PopoverContent
            className="w-80 p-0 rounded-xl overflow-hidden shadow-xl border-border/50 z-50"
            align="end"
            sideOffset={8}
          >
            {/* Hero */}
            <div className="bg-gradient-to-br from-primary/[0.06] to-primary/[0.02] px-4 py-5 flex items-start gap-3 border-b border-border/40">
              <img
                src={CLOCK_ICON}
                alt={t("common:chrome.ai_time_saving")}
                draggable={false}
                className="size-10 object-contain shrink-0 mt-0.5"
              />
              <div>
                <p className="text-[13px] font-semibold text-foreground">{t("common:chrome.ai_saves_your_time")}</p>
                <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                  {t(
                    "common:chrome.every_time_you_use_an_ai_feature_we_track_how_many_minut",
                  )}
                </p>
              </div>
            </div>

            {/* CTA */}
            {hasSubscriptionAccess && (
              <div className="border-t border-border/40">
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      "/company/subscriptions/ai-token-usage-guidelines",
                    )
                  }
                  className="flex w-full items-center justify-between px-4 py-2.5 text-[11px] font-medium text-primary hover:bg-primary/5 transition-colors group"
                >
                  {t("common:chrome.explore_ai_features")}
                  <ArrowRight
                    size={11}
                    className="transition-transform group-hover:translate-x-0.5 rtl-flip"
                  />
                </button>
              </div>
            )}
          </PopoverContent>
        </PopoverPortal>
      </Popover>
    </div>
  );
};
